import { Fragment, type Node } from "@tiptap/pm/model";
import { parseVariablesIntoNodes } from "./parseVariablesIntoNodes";
import type { PasteTransformInternalConfig } from "./PasteTransform";

function splitUnicodeLineBreaks(node: Node) {
  const lineNodes: Array<Node> = [];

  const textLines = node.text?.split("\u2028");
  textLines?.forEach((textLine, lineIndex) => {
    if (textLine) {
      lineNodes.push(node.type.schema.text(textLine));
    }
    // add linebreak if this is not the last line
    if (lineIndex < textLines.length - 1) {
      lineNodes.push(node.type.schema.node("hardBreak"));
    }
  });

  return lineNodes;
}

interface GetTransformedParagraphsProps {
  fragment: Fragment;
  config: PasteTransformInternalConfig;
}

export function getTransformedParagraphs({
  fragment,
  config
}: GetTransformedParagraphsProps) {
  const collectedNodes: Array<Node> = [];

  fragment.forEach((node, _, nodeIndex) => {
    if (node.type.name === "paragraph") {
      // recursively treat paragraph nodes
      const childFragments = getTransformedParagraphs({
        fragment: node.content,
        config
      });
      // join all parts to make editor content into single paragraph (using linebreaks instead)
      childFragments.forEach((childFragment) =>
        collectedNodes.push(childFragment)
      );
      // add linebreak if this is not the last paragraph
      if (nodeIndex < fragment.childCount - 1) {
        collectedNodes.push(node.type.schema.node("hardBreak"));
      }
    } else if (node.type.name === "text") {
      // edge-case: unicode line breaks are not explicitly converted into paragraphs on paste,
      // so we need to handle this ourselves
      const lines = splitUnicodeLineBreaks(node);

      lines.forEach((line) => {
        // variables and custom values need to be explicit editor nodes, so parse these parts out
        const lineNodes = parseVariablesIntoNodes({ line, config });
        collectedNodes.push(...lineNodes);
      });
    } else {
      // copy all other nodes as-is
      collectedNodes.push(
        node.copy(getTransformedParagraphs({ fragment: node.content, config }))
      );
    }
  });

  return Fragment.fromArray(collectedNodes);
}
